import { useCallback, useEffect, useState } from "react"

// Safari (and older WebKit builds) only expose the prefixed fullscreen API, so
// every access below probes both the standard and the `webkit` spelling.
type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null
  webkitFullscreenEnabled?: boolean
  webkitExitFullscreen?: () => Promise<void> | void
}

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void
}

// Safari < 14 only has the deprecated addListener/removeListener pair.
type LegacyMediaQueryList = MediaQueryList & {
  addListener?: (listener: () => void) => void
  removeListener?: (listener: () => void) => void
}

// `blocked` is true when the toggle left the screen covered anyway — see
// `performToggle`, the one case a page cannot talk its way out of.
export interface FullscreenToggleResult {
  blocked: boolean
  isFullscreen: boolean
}

const BROWSER_FULLSCREEN_QUERY = "(display-mode: fullscreen)"

const getFullscreenElement = () => {
  const doc = document as FullscreenDocument
  return doc.fullscreenElement || doc.webkitFullscreenElement || null
}

const getBrowserFullscreenQuery = () =>
  (window.matchMedia?.(BROWSER_FULLSCREEN_QUERY) as LegacyMediaQueryList | undefined) ?? null

// The Electron build owns its window, so it can toggle the real thing instead
// of the page-level Fullscreen API. Only present in the desktop (Steam) build.
const getWindowApi = () => {
  const api = typeof window !== "undefined" ? window.electronApi : undefined
  return api?.toggleWindowFullscreen && api.isWindowFullscreen ? api : undefined
}

// F11 puts the *browser* into fullscreen without going through the Fullscreen
// API, so `fullscreenElement` stays null even though the page owns the screen.
// Browsers report the page as `display-mode: fullscreen` in that state; the
// viewport measurement is a fallback for the ones that don't. Note this also
// matches during our own fullscreen — only the absence of a fullscreen element
// alongside it means the browser is the one holding the screen.
const isBrowserFullscreen = () => {
  if (getBrowserFullscreenQuery()?.matches) return true
  const screen = window.screen
  // Embedded/headless contexts report a 0x0 screen, which no viewport matches.
  if (!screen?.height || !screen.width) return false
  // Only a viewport covering the whole screen counts — a merely maximised
  // window still loses the title bar and the taskbar.
  return Math.abs(window.innerHeight - screen.height) <= 2 && Math.abs(window.innerWidth - screen.width) <= 2
}

const isAnyFullscreen = () => !!getFullscreenElement() || isBrowserFullscreen()

// Fullscreen the browser itself started, which no page API can end.
const isBrowserOwnedFullscreen = () => !getFullscreenElement() && isBrowserFullscreen()

// Leaving fullscreen resizes the window, and the media query and viewport only
// report the new state once that has been painted.
const settle = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))

const performToggle = async (): Promise<FullscreenToggleResult> => {
  const windowApi = getWindowApi()
  if (windowApi) {
    try {
      return { blocked: false, isFullscreen: !!(await windowApi.toggleWindowFullscreen!()) }
    } catch {
      // Bridge failures leave the window as it was.
      return { blocked: false, isFullscreen: isAnyFullscreen() }
    }
  }

  const doc = document as FullscreenDocument
  const el = document.documentElement as FullscreenElement
  const requestFullscreen = () => Promise.resolve((el.requestFullscreen || el.webkitRequestFullscreen)?.call(el))
  const exitFullscreen = () => Promise.resolve((doc.exitFullscreen || doc.webkitExitFullscreen)?.call(doc))

  // Rejections are benign (the user dismissed the permission, or already left
  // via Esc); the listeners in the hook keep the state correct either way.
  if (getFullscreenElement()) {
    await exitFullscreen().catch(() => {})
    await settle()
    // Chromium hands the page back to whatever fullscreen it interrupted, so
    // exiting on top of an F11 session lands right back in it.
    const stillFullscreen = isBrowserFullscreen()
    return { blocked: stillFullscreen, isFullscreen: stillFullscreen }
  }

  if (isBrowserFullscreen()) {
    // Fullscreen entered with F11 belongs to the browser, and `exitFullscreen`
    // only undoes fullscreen the API itself entered. Claiming it first and
    // dropping it is the best a page can try; Chromium restores the F11 session
    // regardless, which is what `blocked` reports back. The F11 interceptor
    // below keeps players out of this state in the first place.
    await requestFullscreen()
      .then(exitFullscreen)
      .catch(() => {})
    await settle()
    const stillFullscreen = isAnyFullscreen()
    return { blocked: stillFullscreen, isFullscreen: stillFullscreen }
  }

  await requestFullscreen().catch(() => {})
  return { blocked: false, isFullscreen: true }
}

const isFullscreenSupported = () => {
  if (getWindowApi()) return true
  const doc = document as FullscreenDocument
  return !!(doc.fullscreenEnabled || doc.webkitFullscreenEnabled)
}

// `lock` is missing on the browsers that expose `screen.orientation` and
// nothing else — iOS Safari most of all — so the property, not the interface,
// is what decides whether we can offer to rotate the game.
type LockableOrientation = ScreenOrientation & {
  lock?: (orientation: OrientationLockType) => Promise<void>
}

const getOrientationApi = () => {
  const orientation = window.screen?.orientation as LockableOrientation | undefined
  return typeof orientation?.lock === "function" ? orientation : undefined
}

// A browser can only lock the orientation of a document that owns the screen,
// and only fullscreen gives it that. Both halves have to exist before a button
// promises the player something it can't deliver.
export const canRequestLandscape = () => !!getOrientationApi() && isFullscreenSupported()

// Must be called straight from a tap: `requestFullscreen` needs the gesture,
// and the lock needs the fullscreen. Returns whether the screen actually turned
// — a `false` leaves the caller to fall back to asking the player to rotate.
export const requestLandscape = async (): Promise<boolean> => {
  const orientation = getOrientationApi()
  if (!orientation) return false

  if (!getFullscreenElement()) {
    const el = document.documentElement as FullscreenElement
    try {
      await Promise.resolve((el.requestFullscreen || el.webkitRequestFullscreen)?.call(el))
    } catch {
      // Dismissed or blocked — without fullscreen the lock cannot succeed.
      return false
    }
  }

  try {
    await orientation.lock!("landscape")
    return true
  } catch {
    // Desktop browsers reject the lock outright, and some mobile ones only
    // pretend to implement it. Whatever fullscreen we took stays: it costs the
    // player nothing, and dropping it would flash the screen for no reason.
    return false
  }
}

// Chromium and Firefox hand F11 to the page before acting on it, so cancelling
// the event keeps the browser out of its own fullscreen and leaves exactly one
// fullscreen state — ours — for the button and the icon to track.
const handleF11 = (event: KeyboardEvent) => {
  if (event.key !== "F11" || event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) return
  if (event.defaultPrevented) return
  // The desktop build's F11 already toggles the same window the button does.
  if (getWindowApi()) return
  // Swallowing F11 while the browser holds the screen would lock the player in
  // with no way out. Let it through; from the next press on they are in sync.
  if (isBrowserOwnedFullscreen()) return
  event.preventDefault()
  void performToggle()
}

// One interceptor for the app, however many components ask for it.
let f11Subscribers = 0

const registerF11Interceptor = () => {
  // Capture, so a game-wide key handler can't swallow F11 before us.
  if (++f11Subscribers === 1) window.addEventListener("keydown", handleF11, true)
  return () => {
    if (--f11Subscribers === 0) window.removeEventListener("keydown", handleF11, true)
  }
}

// Mount this once near the root: F11 has to mean "our fullscreen" everywhere,
// not only on screens that happen to render the toggle button.
export const useFullscreenHotkey = () => {
  useEffect(registerF11Interceptor, [])
}

export const useFullscreen = () => {
  const [isFullscreen, setIsFullscreen] = useState(isAnyFullscreen)
  // Browsers that block the API outright (iOS Safari on iPhone) shouldn't get a
  // button that can't do anything.
  const [supported] = useState(isFullscreenSupported)

  useFullscreenHotkey()

  // The user can also leave fullscreen with Esc, and F11 still reaches the
  // browser in the states we deliberately don't intercept, so the icon follows
  // the browser rather than our own click.
  useEffect(() => {
    const windowApi = getWindowApi()
    let cancelled = false
    const sync = () => {
      // In the desktop build the main process is the only authority: an F11 the
      // renderer never sees still moves the real window.
      if (windowApi) {
        windowApi
          .isWindowFullscreen!()
          .then((value) => !cancelled && setIsFullscreen(!!value))
          .catch(() => {})
        return
      }
      setIsFullscreen(isAnyFullscreen())
    }
    document.addEventListener("fullscreenchange", sync)
    document.addEventListener("webkitfullscreenchange", sync)
    // F11 fires no fullscreenchange at all: the display-mode switch and the
    // viewport resize are the only signals it leaves behind.
    window.addEventListener("resize", sync)
    const query = getBrowserFullscreenQuery()
    if (query?.addEventListener) query.addEventListener("change", sync)
    else query?.addListener?.(sync)
    const unsubscribeWindowApi = windowApi?.onWindowFullscreenChange?.((value) => setIsFullscreen(!!value))
    // A fullscreen change can land between the first render and this effect.
    sync()
    return () => {
      cancelled = true
      document.removeEventListener("fullscreenchange", sync)
      document.removeEventListener("webkitfullscreenchange", sync)
      window.removeEventListener("resize", sync)
      if (query?.removeEventListener) query.removeEventListener("change", sync)
      else query?.removeListener?.(sync)
      unsubscribeWindowApi?.()
    }
  }, [])

  const toggle = useCallback(async () => {
    const result = await performToggle()
    setIsFullscreen(result.isFullscreen)
    return result
  }, [])

  return { isFullscreen, supported, toggle }
}
